import type { TimeEntryFilters } from '@/store/time-entry-filters.store'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { timeEntryApi } from '@/lib/api/time-entries.api'
import { useOrganizationStore } from '@/store/organization.store'
import { toDateOnlyString } from '@/utils/date'
import { useQuery } from '@tanstack/react-query'

import type {
  PaginatedResponse,
  PaginationParams,
  TimeConflictGroup,
  TimeEntry,
} from '@scrambl/shared'
import { createPaginationMeta, TimeEntrySortFieldEnum } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

/**
 * Hook to fetch time entries with filters
 */
export function useTimeEntriesQuery(
  filters?: TimeEntryFilters,
  options?: {
    enabled?: boolean
    allPages?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()
  const { timezone: userTimezone } = useUserTimezone()
  const isExternalUser = currentOrganization?.isExternalAccess === true

  return useQuery<PaginatedResponse<TimeEntry>>({
    queryKey: queryKeys.timeEntries.list({
      ...(filters as Record<string, unknown>),
      timezone: userTimezone,
      exhaustive: options?.allPages || undefined,
    }),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      // Convert filters to API params
      const params: Partial<PaginationParams> & Record<string, any> = {
        page: filters?.page || 1,
        limit: filters?.limit || 50,
        sortBy: filters?.sortBy,
        sortOrder: filters?.sortOrder,
        timezone: userTimezone,
      }

      // Add description filter if it exists and is not empty
      if (filters?.description && filters.description.trim() !== '') {
        params.description = filters.description.trim()
      }

      // Add status filter if it exists and is not empty
      if (filters?.status && (filters.status as string) !== '') {
        params.status = filters.status
      }

      // Add billable filter if it exists
      if (filters?.billable !== undefined) {
        params.billable = filters.billable
      }

      // Add userId filter if it exists
      if (filters?.userId && filters.userId.trim() !== '') {
        params.userId = filters.userId.trim()
      }

      // Add entityId filter if it exists
      if (filters?.entityId && filters.entityId.trim() !== '') {
        params.entityId = filters.entityId.trim()
      }

      // Add projectId filter if it exists
      if (filters?.projectId && filters.projectId.trim() !== '') {
        params.projectId = filters.projectId.trim()
      }

      // Add taskId filter if it exists
      if (filters?.taskId && filters.taskId.trim() !== '') {
        params.taskId = filters.taskId.trim()
      }

      // Add moduleType filter if it exists
      if (filters?.moduleType && filters.moduleType.trim() !== '') {
        params.moduleType = filters.moduleType.trim()
      }

      // Add date range filters if they exist
      if (filters?.dateRange) {
        if (filters.dateRange.from) {
          params.startDate = toDateOnlyString(filters.dateRange.from)
        }
        if (filters.dateRange.to) {
          params.endDate = toDateOnlyString(filters.dateRange.to)
        }
      }

      return options?.allPages
        ? timeEntryApi.getAllTimeEntries(params, 200)
        : timeEntryApi.getTimeEntries(params)
    },
    enabled:
      options?.enabled !== false &&
      !!currentOrganization?.id &&
      !isExternalUser,
    placeholderData: previous => previous,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch a single time entry by ID
 */
export function useTimeEntryQuery(
  id: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()
  const isExternalUser = currentOrganization?.isExternalAccess === true

  return useQuery<TimeEntry>({
    queryKey: queryKeys.timeEntries.detail(id!),
    queryFn: async () => {
      if (!id) {
        throw new Error('Time entry ID is required')
      }
      return timeEntryApi.getTimeEntry(id)
    },
    enabled:
      (options?.enabled !== false &&
        !!id &&
        !!currentOrganization?.id &&
        !isExternalUser) ??
      false,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useTaskTimeTotalQuery(
  taskId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()
  const isExternalUser = currentOrganization?.isExternalAccess === true

  return useQuery<{ taskId: string; totalHours: number; entryCount: number }>({
    queryKey: [
      ...queryKeys.timeEntries.all,
      'task-total',
      taskId ?? 'no-task',
    ] as const,
    queryFn: async () => {
      if (!taskId) {
        throw new Error('Task ID is required')
      }
      return timeEntryApi.getTaskTimeTotal(taskId)
    },
    enabled:
      options?.enabled !== false &&
      !!taskId &&
      !!currentOrganization?.id &&
      !isExternalUser,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch active timer for current user
 */
export function useActiveTimerQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()
  const organizationId = currentOrganization?.id
  const isExternalUser = currentOrganization?.isExternalAccess === true
  const enabled =
    options?.enabled !== false && !!organizationId && !isExternalUser

  return useQuery<TimeEntry | null>({
    queryKey: queryKeys.timeEntries.active(organizationId),
    queryFn: async () => {
      // Guard against race conditions where refetchInterval fires without org context
      if (!organizationId) {
        return null
      }
      return timeEntryApi.getActiveTimer()
    },
    enabled,
    refetchInterval: enabled ? 30000 : false, // Refetch every 30 seconds for active timer
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useTimerSessionEntriesQuery(
  timerSessionId: string | null | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()
  const isExternalUser = currentOrganization?.isExternalAccess === true

  return useQuery<TimeEntry[]>({
    queryKey: queryKeys.timeEntries.list({
      timerSessionId,
      sortBy: TimeEntrySortFieldEnum.START_TIME,
      sortOrder: 'asc',
      limit: 100,
    }),
    queryFn: async () => {
      if (!timerSessionId) {
        return []
      }

      const response = await timeEntryApi.getTimeEntries({
        timerSessionId,
        page: 1,
        limit: 100,
        sortBy: TimeEntrySortFieldEnum.START_TIME,
        sortOrder: 'asc',
      } as Record<string, unknown>)

      return response.data || []
    },
    enabled:
      options?.enabled !== false &&
      !!timerSessionId &&
      !!currentOrganization?.id &&
      !isExternalUser,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch team time entries (all members in organization)
 */
type TeamTimeEntryFilters = Partial<TimeEntryFilters> & {
  startDate?: string
  endDate?: string
}

export function useTeamTimeEntriesQuery(
  filters?: TeamTimeEntryFilters,
  options?: {
    enabled?: boolean
    allPages?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<
    PaginatedResponse<TimeEntry> & {
      totals?: {
        totalHours: number
        billableHours: number
        nonBillableHours: number
        totalBillableAmount: number
        totalCostAmount: number
      }
    }
  >({
    queryKey: [
      ...queryKeys.timeEntries.all,
      'team',
      filters,
      options?.allPages ? 'all-pages' : 'single-page',
    ] as const,
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const params: Record<string, any> = {
        page: filters?.page || 1,
        limit: filters?.limit || 1000,
      }

      if (filters?.description && filters.description.trim() !== '') {
        params.description = filters.description.trim()
      }

      // Multi-select filters (comma-separated for API)
      // Only use array fields — old single-value fields (status, userId, entityId, projectId)
      // may contain stale values from localStorage and are not reflected in the UI.
      if (filters?.statuses && filters.statuses.length > 0) {
        params.status = filters.statuses.join(',')
      }

      if (filters?.statusTier) {
        params.statusTier = filters.statusTier
      }

      if (filters?.billable !== undefined) {
        params.billable = filters.billable
      }

      if (filters?.userIds && filters.userIds.length > 0) {
        params.memberId = filters.userIds.join(',')
      }

      if (filters?.entityIds && filters.entityIds.length > 0) {
        params.entityId = filters.entityIds.join(',')
      }

      if (filters?.projectIds && filters.projectIds.length > 0) {
        params.projectId = filters.projectIds.join(',')
      }

      if (filters?.taskId && filters.taskId.trim() !== '') {
        params.taskId = filters.taskId.trim()
      }

      if (filters?.moduleType && filters.moduleType.trim() !== '') {
        params.moduleType = filters.moduleType.trim()
      }

      if (filters?.sortBy) {
        params.sortBy = filters.sortBy
      }

      if (filters?.sortOrder) {
        params.sortOrder = filters.sortOrder
      }

      if (filters?.dateRange) {
        if (filters.dateRange.from) {
          params.startDate = toDateOnlyString(filters.dateRange.from)
        }
        if (filters.dateRange.to) {
          params.endDate = toDateOnlyString(filters.dateRange.to)
        }
      }

      if (filters?.startDate) {
        params.startDate = filters.startDate
      }

      if (filters?.endDate) {
        params.endDate = filters.endDate
      }

      const response = options?.allPages
        ? await timeEntryApi.getAllTeamEntries(params, 200)
        : await timeEntryApi.getTeamEntries(params)
      return {
        data: response.data,
        meta: createPaginationMeta(
          options?.allPages ? 1 : params.page,
          options?.allPages ? Math.max(response.data.length, 1) : params.limit,
          response.total,
        ),
        totals: response.totals,
      }
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch server-aggregated per-member summary for team time entries.
 * Powers the Summary report — far lighter than fetching full entry list.
 */
export function useTeamSummaryQuery(
  filters?: TeamTimeEntryFilters,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: [...queryKeys.timeEntries.all, 'team-summary', filters] as const,
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')

      const params: Record<string, any> = {}
      if (filters?.description?.trim())
        params.description = filters.description.trim()
      if (filters?.statuses?.length) params.status = filters.statuses.join(',')
      if (filters?.statusTier) params.statusTier = filters.statusTier
      if (filters?.billable !== undefined) params.billable = filters.billable
      if (filters?.userIds?.length) params.memberId = filters.userIds.join(',')
      if (filters?.entityIds?.length)
        params.entityId = filters.entityIds.join(',')
      if (filters?.projectIds?.length)
        params.projectId = filters.projectIds.join(',')
      if (filters?.taskId?.trim()) params.taskId = filters.taskId.trim()
      if (filters?.moduleType?.trim())
        params.moduleType = filters.moduleType.trim()
      if (filters?.dateRange?.from)
        params.startDate = toDateOnlyString(filters.dateRange.from)
      if (filters?.dateRange?.to)
        params.endDate = toDateOnlyString(filters.dateRange.to)
      if (filters?.startDate) params.startDate = filters.startDate
      if (filters?.endDate) params.endDate = filters.endDate

      return timeEntryApi.getTeamSummary(params)
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch the full org client/project lists for the Reports filter
 * dropdowns. Gated on TIME_ENTRY access (not PROJECT/ENTITY record access) so any
 * role that can view timesheet reports — including external members with
 * time-entry access — can filter by client/project.
 */
export function useTeamFilterOptionsQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery({
    queryKey: [...queryKeys.timeEntries.all, 'team-filter-options'] as const,
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return timeEntryApi.getTeamFilterOptions()
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch time entries for a specific member
 */
export function useMemberTimeEntriesQuery(
  memberId: string | undefined,
  filters?: { limit?: number; sortBy?: string; sortOrder?: string },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<PaginatedResponse<TimeEntry>>({
    queryKey: [
      ...queryKeys.timeEntries.all,
      'member',
      memberId,
      filters,
    ] as const,
    queryFn: async () => {
      if (!currentOrganization?.id || !memberId) {
        throw new Error('Organization and member ID are required')
      }

      const params: Record<string, any> = {
        memberId,
        page: 1,
        limit: filters?.limit || 10,
      }

      if (filters?.sortBy) {
        params.sortBy = filters.sortBy
      }

      if (filters?.sortOrder) {
        params.sortOrder = filters.sortOrder
      }

      const response = await timeEntryApi.getTeamEntries(params)
      return {
        data: response.data,
        meta: createPaginationMeta(params.page, params.limit, response.total),
      }
    },
    enabled:
      options?.enabled !== false && !!memberId && !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch the current user's overlapping (double-tracked) time-entry
 * conflicts within a date range. Used by the My Time + own-timesheet warnings.
 */
export function useMyTimeConflictsQuery(
  range: { startDate?: string; endDate?: string },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  const isExternalUser = currentOrganization?.isExternalAccess === true

  return useQuery<{ groups: TimeConflictGroup[] }>({
    queryKey: [
      ...queryKeys.timeEntries.all,
      'conflicts',
      'me',
      range.startDate ?? null,
      range.endDate ?? null,
    ] as const,
    queryFn: async () => {
      if (!range.startDate || !range.endDate) {
        return { groups: [] }
      }
      return timeEntryApi.getMyTimeConflicts({
        startDate: range.startDate,
        endDate: range.endDate,
      })
    },
    enabled:
      options?.enabled !== false &&
      !!range.startDate &&
      !!range.endDate &&
      !!currentOrganization?.id &&
      !isExternalUser,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Hook to fetch a specific team member's overlapping time-entry conflicts.
 * Used by the manager-facing Timesheet detail warning.
 */
export function useTeamTimeConflictsQuery(
  params: { userId?: string; startDate?: string; endDate?: string },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<{ groups: TimeConflictGroup[] }>({
    queryKey: [
      ...queryKeys.timeEntries.all,
      'conflicts',
      'team',
      params.userId ?? null,
      params.startDate ?? null,
      params.endDate ?? null,
    ] as const,
    queryFn: async () => {
      if (!params.userId || !params.startDate || !params.endDate) {
        return { groups: [] }
      }
      return timeEntryApi.getTeamTimeConflicts({
        userId: params.userId,
        startDate: params.startDate,
        endDate: params.endDate,
      })
    },
    enabled:
      options?.enabled !== false &&
      !!params.userId &&
      !!params.startDate &&
      !!params.endDate &&
      !!currentOrganization?.id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * useTimeEntriesQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const timeEntriesListQuery = useTimeEntriesQuery
